import React, { useEffect, useMemo, useState } from 'react';
import {
    AutoComplete,
    Button,
    Card,
    Col,
    Drawer,
    Form,
    Input,
    Modal,
    Row,
    Select,
    Space,
    Switch,
    Table,
    Tag,
    Typography
} from 'antd';
import {
    EditOutlined,
    EyeOutlined,
    PlusOutlined,
    ReloadOutlined,
    SearchOutlined
} from '@ant-design/icons';
import { connect } from 'react-redux';
import { AppState } from '../store';
import { getAllLegalDocuments, postLegalDocument, putLegalDocument } from '../store/user/thunks';
import { LegalDocument } from '../store/user/types';
import {
    formatUtcDate,
    getOrganizationToday,
    organizationInputToUtc,
    utcInstantToOrganizationInput
} from '../services/dateTime';

const { Title, Text } = Typography;

const getCurrentVersion = () => getOrganizationToday();

const getInitialValues = (): LegalDocument => {
    const now = new Date().toISOString();

    return {
        code: '',
        name: '',
        url: '',
        description: '',
        status: 'ACTIVO',
        optional: false,
        current: true,
        version: getCurrentVersion(),
        contentText: '',
        contentType: 'TEXT',
        effectiveFrom: utcInstantToOrganizationInput(now)
    };
};

const LegalDocumentManagement: React.FC<any> = (props: any) => {
    const [form] = Form.useForm();
    const [documents, setDocuments] = useState<LegalDocument[]>([]);
    const [search, setSearch] = useState<string>('');
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [selectedDocument, setSelectedDocument] = useState<LegalDocument | null>(null);
    const [preview, setPreview] = useState<{ title: string; contentText: string; contentType: 'TEXT' | 'HTML'; url?: string } | null>(null);

    const filteredDocuments = useMemo(() => {
        const value = search.trim().toLowerCase();
        if (!value) {
            return documents;
        }

        return documents.filter(document =>
            String(document.code || '').toLowerCase().includes(value)
            || String(document.name || '').toLowerCase().includes(value)
            || String(document.version || '').toLowerCase().includes(value)
            || String(document.status || '').toLowerCase().includes(value)
            || String(document.description || '').toLowerCase().includes(value));
    }, [documents, search]);

    const codeOptions = useMemo(() => {
        return Array.from(new Set(documents.map(document => document.code).filter(Boolean)))
            .sort((codeA, codeB) => codeA.localeCompare(codeB))
            .map(code => ({ value: code, label: code }));
    }, [documents]);

    const loadDocuments = async () => {
        const response = await props.getAllLegalDocuments();
        setDocuments(response || []);
    };

    useEffect(() => {
        loadDocuments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openCreateModal = () => {
        setSelectedDocument(null);
        form.resetFields();
        form.setFieldsValue(getInitialValues());
        setIsModalOpen(true);
    };

    const openEditModal = (document: LegalDocument) => {
        setSelectedDocument(document);
        form.resetFields();
        form.setFieldsValue({
            ...document,
            effectiveFrom: utcInstantToOrganizationInput(document.effectiveFrom)
        });
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedDocument(null);
    };

    const handleSave = async () => {
        const values = await form.validateFields();
        const request: LegalDocument = {
            id: selectedDocument?.id || null,
            code: values.code,
            name: values.name,
            url: values.url,
            description: values.description || null,
            status: values.status,
            optional: !!values.optional,
            current: !!values.current,
            version: values.version,
            contentText: values.contentText,
            contentType: values.contentType,
            effectiveFrom: organizationInputToUtc(values.effectiveFrom)
        };

        const response = selectedDocument
            ? await props.putLegalDocument(request)
            : await props.postLegalDocument(request);

        if (response) {
            await loadDocuments();
            closeModal();
        }
    };

    const columns = [
        {
            title: 'Documento',
            dataIndex: 'name',
            key: 'name',
            render: (value: string, row: LegalDocument) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{value}</Text>
                    <Text type="secondary">{row.code}</Text>
                </Space>
            )
        },
        {
            title: 'Version',
            dataIndex: 'version',
            key: 'version'
        },
        {
            title: 'Vigente',
            dataIndex: 'current',
            key: 'current',
            render: (value: boolean) => <Tag color={value ? 'green' : 'default'}>{value ? 'SI' : 'NO'}</Tag>
        },
        {
            title: 'Estado',
            dataIndex: 'status',
            key: 'status',
            render: (value: string) => <Tag color={value === 'ACTIVO' ? 'green' : 'red'}>{value}</Tag>
        },
        {
            title: 'Tipo',
            dataIndex: 'optional',
            key: 'optional',
            render: (value: boolean) => value ? 'Opcional' : 'Obligatorio'
        },
        {
            title: 'Vigente desde',
            dataIndex: 'effectiveFrom',
            key: 'effectiveFrom',
            render: (value: string) => formatUtcDate(value)
        },
        {
            title: '',
            key: 'actions',
            align: 'right' as const,
            render: (_: unknown, row: LegalDocument) => (
                <Space>
                    <Button icon={<EyeOutlined />} onClick={() => setPreview({
                        title: `${row.name} ${row.version}`,
                        contentText: row.contentText || '',
                        contentType: row.contentType || 'TEXT',
                        url: row.url
                    })}>
                        Ver
                    </Button>
                    <Button icon={<EditOutlined />} onClick={() => openEditModal(row)}>
                        Editar
                    </Button>
                </Space>
            )
        }
    ];

    return (
        <div className="tbc-admin-page">
            <Space direction="vertical" size={18} style={{ width: '100%' }}>
                <div>
                    <Title level={3} style={{ marginBottom: 4 }}>Documentos legales</Title>
                    <Text type="secondary">Administra versiones de terminos, privacidad y documentos que requieren aceptacion.</Text>
                </div>

                <Card>
                    <Row gutter={[12, 12]} align="bottom">
                        <Col xs={24} md={12}>
                            <Text strong>Buscar</Text>
                            <Input
                                allowClear
                                prefix={<SearchOutlined />}
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                placeholder="Codigo, nombre, version o estado"
                                style={{ marginTop: 6 }}
                            />
                        </Col>
                        <Col xs={24} md={6}>
                            <Button icon={<ReloadOutlined />} block onClick={loadDocuments}>
                                Actualizar
                            </Button>
                        </Col>
                        <Col xs={24} md={6}>
                            <Button type="primary" icon={<PlusOutlined />} block onClick={openCreateModal}>
                                Nuevo documento
                            </Button>
                        </Col>
                    </Row>
                </Card>

                <Card>
                    <Table
                        rowKey={(row) => row.id || `${row.code}-${row.version}`}
                        columns={columns}
                        dataSource={filteredDocuments}
                        scroll={{ x: 980 }}
                        pagination={{ pageSize: 8 }}
                    />
                </Card>

                <Modal
                    open={isModalOpen}
                    title={selectedDocument ? 'Editar documento legal' : 'Nuevo documento legal'}
                    okText={selectedDocument ? 'Actualizar' : 'Crear'}
                    cancelText="Cancelar"
                    width={920}
                    onCancel={closeModal}
                    onOk={handleSave}
                    destroyOnHidden
                >
                    <Form form={form} layout="vertical" initialValues={getInitialValues()}>
                        <Row gutter={[16, 0]}>
                            <Col xs={24} md={8}>
                                <Form.Item name="code" label="Codigo" rules={[{ required: true, message: 'Ingrese el codigo' }]}>
                                    <AutoComplete
                                        options={codeOptions}
                                        filterOption={(inputValue, option) =>
                                            String(option?.value || '').toUpperCase().includes(inputValue.toUpperCase())
                                        }
                                        onChange={(value) => form.setFieldsValue({ code: String(value || '').toUpperCase().replace(/\s+/g, '_') })}
                                        placeholder="TERMS_CONDITIONS"
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={10}>
                                <Form.Item name="name" label="Nombre" rules={[{ required: true, message: 'Ingrese el nombre' }]}>
                                    <Input placeholder="Terminos y condiciones" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={6}>
                                <Form.Item name="version" label="Version" rules={[{ required: true, message: 'Ingrese la version' }]}>
                                    <Input placeholder="2026-07-14" />
                                </Form.Item>
                            </Col>
                            <Col xs={24}>
                                <Form.Item name="description" label="Descripcion">
                                    <Input.TextArea rows={2} placeholder="Descripcion corta del documento" />
                                </Form.Item>
                            </Col>
                            <Col xs={24}>
                                <Form.Item name="url" label="URL publica" rules={[{ required: true, message: 'Ingrese la URL publica' }]}>
                                    <Input placeholder="https://thebluecoding.com/bluemedix/legal/terminos-condiciones" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item name="contentType" label="Tipo de contenido" rules={[{ required: true, message: 'Seleccione el tipo de contenido' }]}>
                                    <Select
                                        options={[
                                            { value: 'TEXT', label: 'Texto' },
                                            { value: 'HTML', label: 'HTML' }
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={6}>
                                <Form.Item name="effectiveFrom" label="Vigente desde" rules={[{ required: true, message: 'Ingrese la fecha' }]}>
                                    <Input type="datetime-local" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={6}>
                                <Form.Item name="status" label="Estado" rules={[{ required: true }]}>
                                    <Select
                                        options={[
                                            { value: 'ACTIVO', label: 'Activo' },
                                            { value: 'INACTIVO', label: 'Inactivo' }
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item name="current" label="Version vigente" valuePropName="checked">
                                    <Switch checkedChildren="SI" unCheckedChildren="NO" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item name="optional" label="Documento opcional" valuePropName="checked">
                                    <Switch checkedChildren="SI" unCheckedChildren="NO" />
                                </Form.Item>
                            </Col>
                            <Col xs={24}>
                                <Form.Item name="contentText" label="Contenido legal" rules={[{ required: true, message: 'Ingrese el contenido legal' }]}>
                                    <Input.TextArea rows={12} placeholder="Pegue aqui el texto o HTML del documento legal" />
                                </Form.Item>
                                <Text type="secondary">El hash se calcula automaticamente al guardar para conservar la huella del contenido aceptado.</Text>
                            </Col>
                        </Row>
                    </Form>
                </Modal>

                <Drawer
                    title={preview?.title || 'Documento legal'}
                    open={!!preview}
                    placement="bottom"
                    height="80vh"
                    onClose={() => setPreview(null)}
                    destroyOnHidden
                    extra={preview ? (
                        <Button href={preview.url} target="_blank" rel="noreferrer" disabled={!preview.url}>
                            Abrir URL publica
                        </Button>
                    ) : null}
                    styles={{
                        body: { padding: 0, background: '#ffffff' }
                    }}
                >
                    {preview?.contentType === 'HTML' && (
                        <iframe
                            title={preview.title}
                            srcDoc={preview.contentText}
                            style={{ width: '100%', height: '100%', border: 0, background: '#ffffff' }}
                        />
                    )}
                    {preview?.contentType !== 'HTML' && preview && (
                        <pre style={{ margin: 0, minHeight: '100%', padding: 24, whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 14, lineHeight: 1.6 }}>
                            {preview.contentText}
                        </pre>
                    )}
                </Drawer>
            </Space>
        </div>
    );
};

const mapStateToProps = (_state: AppState) => ({
});

export default connect(mapStateToProps, {
    getAllLegalDocuments,
    postLegalDocument,
    putLegalDocument
})(LegalDocumentManagement);
